import { Controller } from "@hotwired/stimulus"

// Polls a background job's status and moves the user on when it's done.
//
// The work is queued so it can't block the server (a slow request holds
// Rails' dev lock and freezes the whole site), but the USER still waits on a
// page that shows progress and redirects itself the moment the job finishes.
export default class extends Controller {
  static targets = ["message", "stage"]
  static values = {
    url: String,
    interval: { type: Number, default: 1200 },
    // A CSS colour for a stage that has been reached. Defaults to brand so
    // the importer, which does not pass one, is unaffected.
    accent: { type: String, default: "" },
    // Where to go when the job finishes. Empty means the recipe itself,
    // which is what import wants.
    doneUrl: { type: String, default: "" },
    messages: {
      type: Array,
      default: [
        "Matching ingredients to your foods…",
        "Working out quantities…",
        "Calculating macros from your food data…",
        "Almost there…"
      ]
    }
  }

  connect() {
    this.messageIndex = 0
    this.stageIndex = 0
    this.markStage()
    this.timer = setInterval(() => this.check(), this.intervalValue)
    this.messageTimer = setInterval(() => this.nextMessage(), 2600)
    this.stageTimer = setInterval(() => this.nextStage(), 2600)
  }

  disconnect() {
    this.stop()
  }

  // Advances the visible stage. We cannot observe the model's real progress,
  // so this indicates rather than measures — and holds on the last stage
  // instead of looping, which would imply progress that isn't happening.
  nextStage() {
    if (this.stageIndex >= this.stageTargets.length - 1) return

    this.stageIndex += 1
    this.markStage()
  }

  // THE LIT COLOUR COMES FROM THE VIEW, not from here. This controller is
  // shared with recipe import, where brand orange is right; in the AI flow
  // it made the one screen you sit and watch the one screen that stopped
  // looking like the feature. A default keeps the importer unchanged.
  markStage() {
    const lit = this.accentValue || "var(--color-brand)"

    this.stageTargets.forEach((element, index) => {
      const done = index <= this.stageIndex
      element.classList.toggle("text-ink", done)
      element.classList.toggle("text-ink-faint", !done)
      const dot = element.querySelector("span")
      if (dot) dot.style.backgroundColor = done ? lit : "var(--color-line)"
    })
  }

  nextMessage() {
    if (!this.hasMessageTarget) return
    // Hold on the last one rather than looping — repeating would imply
    // progress we can't see.
    if (this.messageIndex >= this.messagesValue.length - 1) return

    this.messageIndex += 1
    this.messageTarget.textContent = this.messagesValue[this.messageIndex]
  }

  async check() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" }
      })
      if (!response.ok) return

      const data = await response.json()

      if (data.status === "done" && data.recipe_id) {
        this.stop()
        // WHERE DONE GOES IS THE CALLER'S. Import keeps its recipe
        // immediately, so /recipes/:id is right there. Generation has a
        // review screen first — the recipe is a draft nobody has decided
        // to keep, and sending somebody to the library page for it shows
        // them a recipe that is not theirs yet and hides the decision.
        //
        // Defaults to the importer's behaviour so that caller is
        // unchanged.
        window.location.href = this.doneUrlValue || `/recipes/${data.recipe_id}`
      } else if (data.status === "failed") {
        this.stop()
        window.location.reload()
      }
    } catch {
      // A dropped poll is not worth surfacing — the next tick retries, and
      // the page still shows the last known state.
    }
  }

  stop() {
    clearInterval(this.timer)
    clearInterval(this.messageTimer)
    clearInterval(this.stageTimer)
  }
}
